<template>
<div
    v-if="hasDocumentation"
    class="integration-api-docs mb-4"
>

    <!-- Tock v2 (also shown for 'tock' secret type since secrets page uses tock-v2 setup) -->
    <template v-if="integration_type === 'tock-v2' || integration_type === 'tock'">
        <p class="font-weight-bold">Webhook URL(s)</p>
        <p>For reference, the tock <b>reservation</b> webhook URL is:</p>
        <m-code-block>
            https://app.redchirp.com/api/v1/webhook/inbound/tock/reservation
        </m-code-block>
        <p>For reference, the tock <b>guest profile</b> webhook URL is:</p>
        <m-code-block>
            https://app.redchirp.com/api/v1/webhook/inbound/tock/guest-profile
        </m-code-block>
    </template>

    <!-- ShipEngine -->
    <template v-if="integration_type === 'ship-engine'">
        <p>Enabling a ShipEngine integration allows your organization to send detailed shipping automated messages (for example, an automated message when a package that needs a signature is out for delivery.)</p>
        <p><b>Fee Notice:</b> There is a cost of $0.02 (two cents) per package for this data -- this cost is per package, not per message sent.  Therefore, if one package triggered several automated messages (e.g. out for delivery, delivered) you would only be charged one fee of once cent.  These fees only apply to packages that are shipped while the integration is enabled.  You can disable this integration at any time.</p>
    </template>

    <!-- Mailchimp -->
    <template v-if="integration_type === 'mailchimp'">
        <p class="font-weight-bold">How to determine your Mailchimp datacenter</p>
        <ul>
            <li>Sign into your Mailchimp account</li>
            <li>Look at the host name in the URL.</li>
            <li>The datacenter is the word before the first . of the hostname.</li>
            <li>For example, if your url is <code>https://us5.admin.mailchimp.com/</code> then the datacenter is <code>us5</code></li>
        </ul>
        <p class="font-weight-bold mt-4">How to get a Mailchimp API key</p>
        <ul>
            <li>Sign into your Mailchimp account</li>
            <li>In the lower left, click on your name to access your account settings</li>
            <li>On the settings page, near the top, click the "Extras" menu</li>
            <li>From the "Extras" menu choose "API Keys"</li>
            <li>Create an API key and paste the value here</li>
        </ul>
    </template>

    <!-- Corksy -->
    <template v-if="integration_type === 'corksy'">
        <p class="font-weight-bold">API Documentation</p>
        <p>First, generate your API key from the Admin Settings API page:</p>
        <ul>
            <li>Go to: <a href="https://admin.corksy.io/settings/apiaccess" target="_blank">https://admin.corksy.io/settings/apiaccess</a></li>
            <li>Create a new API key for your application</li>
            <li>Copy the new API key into the "API Key" field below</li>
        </ul>
    </template>

    <!-- Vinoshipper -->
    <template v-if="integration_type === 'vinoshipper'">
        <p class="font-weight-bold">API Documentation</p>
        <p>For instructions on creating your Vinoshipper API Key and Secret, please visit: <a href="https://vinoshipper.freshdesk.com/support/solutions/articles/9000214351-how-to-create-an-api-key" target="_blank">Vinoshipper's Documentation on Creating an API Key</a></p>
        <ul>
            <li>First, generate a new API Key and Secret from the Vinoshipper dashboard following the instructions in the API Documentation</li>
            <li>Copy the API Key into the "API Key" field below</li>
            <li>Copy the Secret into the "Secret" field below</li>
        </ul>
    </template>

    <!-- Bottle360 -->
    <template v-if="integration_type === 'bottle360'">
        <p class="font-weight-bold">Setup Instructions</p>
        <p>Bottle360 requires a dedicated system user for RedChirp. This account should not be used for regular user logins.</p>
        <ul>
            <li>Create a dedicated system user in Bottle360 for RedChirp</li>
            <li>Enter the username and password for this system user below</li>
        </ul>
    </template>

    <!-- Copper Peak -->
    <template v-if="integration_type === 'copper-peak'">
        <p class="font-weight-bold">Setup Instructions</p>
        <p>This integration syncs order and shipping data from your InfoPlus WMS system.</p>
        <ul>
            <li>Obtain your InfoPlus WMS API key from Copper Peak</li>
            <li>Enter the API key below</li>
        </ul>
    </template>

</div>
</template>

<script>
export default {
    name: 'integration-api-docs',

    props: {
        integration_type: {
            type: String,
            required: true
        },
        organization_id: {
            type: String,
            default: null
        }
    },

    computed: {
        hasDocumentation() {
            const supported = ['corksy', 'vinoshipper', 'tock', 'tock-v2', 'ship-engine', 'mailchimp', 'bottle360', 'copper-peak'];
            return supported.includes(this.integration_type);
        }
    }
}
</script>

<style lang="scss" scoped>
.integration-api-docs {
    margin-top: 16px;
}
</style>
